/**
 * The container for the first-data screen: the campaign's accounts and its
 * daily cost are the only things the collection has produced so far, and the
 * two reads that answer them.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useCampaignBurnRate, useTrackedAccounts } from '../../campaign/accounts/api/queries';
import { FirstDataView, type Pending } from './FirstDataView';

function settled<T, R>(
  query: UseQueryResult<T, ApiFailureError>,
  pick: (data: T) => R,
): Pending<R> {
  if (query.status === 'pending') return 'pending';
  if (query.status === 'error') return 'failed';
  return pick(query.data);
}

export function FirstData(props: {
  campaignId: string;
  tenantId: string;
  campaignName: string;
  canWrite: boolean;
}): ReactNode {
  const accounts = useTrackedAccounts(props.campaignId);
  const burnRate = useCampaignBurnRate(props.campaignId);

  return (
    <FirstDataView
      campaignId={props.campaignId}
      tenantId={props.tenantId}
      campaignName={props.campaignName}
      canWrite={props.canWrite}
      accounts={settled(accounts, (rows) => {
        const profileNames = [
          ...new Set(rows.flatMap((row) => (row.profile_name === null ? [] : [row.profile_name]))),
        ];
        return {
          accounts: rows.length,
          profiles: profileNames.length,
          sharedProfileName: profileNames.length === 1 ? (profileNames[0] ?? null) : null,
        };
      })}
      creditsPerDay={settled(burnRate, (rate) => {
        const total = rate.totals.estimated_credits_per_day;
        // Nothing enabled means nothing to price, and that is not a cost of zero.
        return total !== null && total > 0 ? total : null;
      })}
    />
  );
}
